import React, { useState, useEffect } from 'react';
import s3Service from '../services/s3Service';

const OFFLINE_MAX_SIZE = 25 * 1024 * 1024; // 25MB cap for offline buffering

const S3MultipleFileUpload = ({
  onFilesUpload,
  accept = "*",
  maxSize = 100 * 1024 * 1024, // 100MB default
  maxFiles = 10, // Maximum files allowed (Laravel default)
  helpText = "Upload documents related to this ticket. Allowed formats: PDF, DOC, DOCX, XLS, XLSX, TXT, CSV, Images, Archives. Maximum 10 files, 100MB per file.",
  folder = "documents"
}) => {
  const [fileInputs, setFileInputs] = useState([{ id: 1, file: null, uploading: false, uploaded: false, error: null }]);
  const [filesCounter, setFilesCounter] = useState(1);

  // Keep the parent in sync with the current set of usable files (uploaded to S3
  // OR buffered offline). Offline entries carry the raw blob so the parent can
  // queue them for background sync.
  useEffect(() => {
    if (!onFilesUpload) return;
    const usable = fileInputs
      .filter((i) => i.uploaded)
      .map((i) => ({
        id: i.id,
        name: i.file?.name,
        size: i.file?.size,
        type: i.file?.type,
        s3Key: i.s3Key,
        s3Url: i.s3Url,
        offline: !!i.offline,
        blob: i.blob,
      }));
    onFilesUpload(usable);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileInputs]);

  const addFileField = () => {
    // Laravel limit: max 10 files
    if (filesCounter >= maxFiles) {
      alert(`Maximum ${maxFiles} files allowed`);
      return;
    }

    const newCounter = filesCounter + 1;
    setFilesCounter(newCounter);
    setFileInputs(prev => [...prev, {
      id: newCounter,
      file: null,
      uploading: false,
      uploaded: false,
      error: null
    }]);
  };

  const removeFileField = (id) => {
    if (fileInputs.length > 1) {
      setFileInputs(prev => prev.filter(input => input.id !== id));
    }
  };

  const handleFileChange = async (id, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file size
    if (file.size > maxSize) {
      setFileInputs(prev => prev.map(input => 
        input.id === id 
          ? { ...input, error: `File size exceeds ${formatFileSize(maxSize)}` }
          : input
      ));
      return;
    }

    // Update file input state
    setFileInputs(prev => prev.map(input =>
      input.id === id
        ? { ...input, file, uploading: true, error: null }
        : input
    ));

    // Buffer the file locally for later sync (offline / on upload failure).
    const bufferOffline = () => {
      if (file.size > OFFLINE_MAX_SIZE) {
        setFileInputs(prev => prev.map(input =>
          input.id === id
            ? { ...input, uploading: false, error: `Offline: file must be under ${OFFLINE_MAX_SIZE / (1024 * 1024)}MB` }
            : input
        ));
        return;
      }
      setFileInputs(prev => prev.map(input =>
        input.id === id
          ? { ...input, uploading: false, uploaded: true, offline: true, blob: file, s3Key: undefined }
          : input
      ));
    };

    const isOnline = typeof navigator === 'undefined' ? true : navigator.onLine;
    if (!isOnline) {
      bufferOffline();
      return;
    }

    try {
      // Upload to S3
      const result = await s3Service.upload(file, folder);

      // Update state with successful upload (keep blob in case a later submit
      // fails on the network and the parent needs to re-buffer).
      setFileInputs(prev => prev.map(input =>
        input.id === id
          ? { ...input, uploading: false, uploaded: true, s3Key: result.key, s3Url: s3Service.getFileUrl(result.key), blob: file }
          : input
      ));
    } catch (error) {
      console.error('S3 upload failed, buffering for offline sync:', error);
      bufferOffline();
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div>
      {/* Laravel-style "Add file" button */}
      <button 
        type="button"
        className="btn btn-default"
        onClick={addFileField}
        style={{ marginBottom: '10px' }}
      >
        Add file
      </button>

      {/* Hidden counter input (Laravel compatibility) */}
      <input type="hidden" name="filesCounter" value={filesCounter} />

      <div className="col-sm-12">
        <div className="form-group">
          <div className="input_file_fields_wrap">
            
            {fileInputs.map((inputField) => (
              <div key={inputField.id} style={{ marginBottom: '3px', display: 'flex', alignItems: 'center' }}>
                <div style={{ flex: 1 }}>
                  <input
                    type="file"
                    name="files[]"
                    className="form-control"
                    accept={accept}
                    onChange={(e) => handleFileChange(inputField.id, e)}
                    disabled={inputField.uploading}
                    style={{
                      opacity: inputField.uploaded ? 0.7 : 1,
                      backgroundColor: inputField.uploaded ? '#d4edda' : '#fff'
                    }}
                  />
                  
                  {/* Upload status */}
                  {inputField.uploading && (
                    <div style={{ fontSize: '12px', color: '#007bff', marginTop: '2px' }}>
                      📤 Uploading to S3...
                    </div>
                  )}
                  
                  {inputField.uploaded && (
                    <div style={{ fontSize: '12px', color: '#28a745', marginTop: '2px' }}>
                      ✅ Uploaded to S3: {inputField.file?.name}
                    </div>
                  )}
                  
                  {inputField.error && (
                    <div style={{ fontSize: '12px', color: '#dc3545', marginTop: '2px' }}>
                      ❌ {inputField.error}
                    </div>
                  )}
                </div>

                {/* Remove button (only show if more than 1 field) */}
                {fileInputs.length > 1 && (
                  <button 
                    type="button"
                    className="remove_field"
                    onClick={() => removeFileField(inputField.id)}
                    style={{ 
                      marginLeft: '10px', 
                      color: '#dc3545', 
                      textDecoration: 'none',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: 'inherit'
                    }}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}

          </div>
        </div>

        {/* Help text */}
        <div style={{ 
          fontSize: '12px', 
          color: '#6c757d', 
          marginTop: '8px',
          lineHeight: '1.4'
        }}>
          {helpText}
        </div>
      </div>
    </div>
  );
};

export default S3MultipleFileUpload;
